import * as React from 'react';
import { partial } from 'rambda';

import { PriceSortQueryValue, isBrowser, Page } from '@packages/common';
import { useDyClient } from '@lib/dy';
import { AppStaticStateContext } from '../AppStaticStateContext';
import {
  fireAddToWishlistEvent,
  fireRemoveFromWishlistEvent,
  fireFilterItemsByNumericFacetEvent,
  fireFilterItemsByNumericRangeFacetEvent,
  fireFilterItemsByStringFacetEvent,
  fireRemoveNumericFilterEvent,
  fireRemoveStringFilterEvent,
  fireClearAllFiltersEvent,
  fireDYIdentifyAPIEvent,
  fireSearchEvent,
  fireSortItemsEvent,
  fireClickoutEvent,
  fireLoginEvent,
  fireNewsletterSubscriptionEvent,
  fireDYSPAPageView,
  createPurchaseEvent,
} from './dynamicYieldEventUtils';

interface DynamicYieldEvents {
  fireAddToWishlistEvent: (wishlistedItemId: string) => void;
  fireRemoveFromWishlistEvent: (wishlistedItemId: string) => void;
  fireFilterItemsByNumericFacetEvent: (facetName: string, value: number) => void;
  fireFilterItemsByNumericRangeFacetEvent: (facetName: string, from: number, to: number) => void;
  fireFilterItemsByStringFacetEvent: (facetName: string, value: string) => void;
  fireRemoveNumericFilterEvent: (facetName: string) => void;
  fireRemoveStringFilterEvent: (facetName: string, value: string) => void;
  fireClearAllFiltersEvent: () => void;
  fireDYIdentifyAPIEvent: () => void;
  fireDYSPAPageView: (pageType: Page, asPath: string, categoryName?: string) => void;
  fireSearchEvent: (searchQuery: string) => void;
  fireSortItemsEvent: (priceSortValue: PriceSortQueryValue) => void;
  fireClickoutEvent: (productId: string) => void;
  fireLoginEvent: (userIdHash: string) => void;
  fireNewsletterSubscriptionEvent: (lowercasedHashedHexEmail: string) => void;
}

export const useDynamicYieldEvents = (): DynamicYieldEvents => {
  const {
    userData: { hash: userIdHash },
    deviceType,
  } = React.useContext(AppStaticStateContext);
  const dyData = isBrowser() ? (window as any)?.DY : {};

  // this SPA event should only be sent client-side
  const fireDYSPAPageViewEvent = (pageType: Page, asPath: string, categoryName?: string) => {
    if (isBrowser()) {
      fireDYSPAPageView(dyData, asPath, pageType, `${categoryName}`);
    }
  };

  return {
    fireAddToWishlistEvent: partial(fireAddToWishlistEvent, dyData),
    fireRemoveFromWishlistEvent: partial(fireRemoveFromWishlistEvent, dyData),
    fireFilterItemsByNumericFacetEvent: (facetName, value) =>
      fireFilterItemsByNumericFacetEvent(dyData, facetName, value),
    fireFilterItemsByNumericRangeFacetEvent: (facetName, from, to) =>
      fireFilterItemsByNumericRangeFacetEvent(dyData, facetName, from, to),
    fireFilterItemsByStringFacetEvent: partial(fireFilterItemsByStringFacetEvent, dyData),
    fireRemoveNumericFilterEvent: partial(fireRemoveNumericFilterEvent, dyData),
    fireRemoveStringFilterEvent: partial(fireRemoveStringFilterEvent, dyData),
    fireClearAllFiltersEvent: () => fireClearAllFiltersEvent(dyData),
    fireDYIdentifyAPIEvent: () => fireDYIdentifyAPIEvent(dyData, userIdHash),
    fireDYSPAPageView: fireDYSPAPageViewEvent,
    fireSearchEvent: partial(fireSearchEvent, dyData),
    fireSortItemsEvent: partial(fireSortItemsEvent, dyData),
    fireClickoutEvent: (productId) => fireClickoutEvent(dyData, deviceType, productId),
    fireLoginEvent: partial(fireLoginEvent, dyData),
    fireNewsletterSubscriptionEvent: partial(fireNewsletterSubscriptionEvent, dyData),
  };
};

//#region For Custom API
interface DynamicYieldApiEvents {
  firePurchaseEvent: (productId: string) => void;
}

export const useDynamicYieldApiEvents = (): DynamicYieldApiEvents => {
  const { deviceType } = React.useContext(AppStaticStateContext);
  const { client, context } = useDyClient();

  return {
    firePurchaseEvent: (productId: string) => {
      client.events({ context, events: [createPurchaseEvent(deviceType, productId)] });
    },
  };
};
//#endregion
